// PUBLICAR EN EL TELÉFONO, a pantalla completa
// (ADR-2026-10-30-publicar-reemplaza-a-herramientas-en-la-barra). La abre la pestaña Publicar de
// la barra de abajo, que ocupa el lugar que era de Herramientas (Herramientas pasó a ser una
// card de Crear).
//
// Dos cards grandes, una por lugar donde sale lo que se crea: Instagram (el calendario de
// posts) y Tienda online (los lanzamientos). Son las mismas rutas que la sidebar de
// escritorio. Pantalla completa que termina arriba de la barra (como Creaciones), que queda
// a la vista, y se sale tocando otro lugar de abajo.
//
// A la derecha de cada card, una ilustración de LO QUE HACE la sección, no el abanico de
// resultados de las cards de Crear (ADR-2026-10-30-las-cards-de-publicar-muestran-la-funcion):
// en Instagram, un calendario con posts programados; en Tienda online, la ficha de un producto
// publicada en sus canales. Dibujadas con HTML y las fotos del rubro, así se ven nítidas a
// cualquier escala y cambian con la marca.

import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { plataformasDe, posterDeClip, type Plataforma } from '../../estilos/CabeceraPorDestino';
import { rubroMarcaCacheado } from '../../../hooks/useRubroMarca';
import { ALTO_NAV_INFERIOR } from './NavInferiorMobile';

const LOGO_INSTAGRAM = '/images/logos_plataformas/instagram.png';
const LOGO_TIENDANUBE = '/images/logos_plataformas/tiendanube.png';
const LOGO_MERCADO_LIBRE = '/images/logos_plataformas/mercadolibre.png';

/** La foto de una pieza del abanico: la foto, o el póster del clip (mismo nombre, `.jpg`). */
const fotoDe = (p: Plataforma): string => p.pieza.foto ?? posterDeClip(p.pieza.video ?? '');

type LugarDePublicacion = {
  id: string;
  nombre: string;
  explicacion: string;
  ruta: string;
  /** Fondo de la card: los tintes de Redes sociales y Catálogos, de donde sale lo que se publica. */
  tinte: string;
  ilustracion: ReactNode;
};

/** Las dos cards. Las fotos de cada ilustración son las del rubro de su card de Crear. */
const lugares = (rubro: string | null): LugarDePublicacion[] => [
  {
    id: 'instagram',
    nombre: 'Instagram',
    explicacion: 'Tu calendario de posts: qué sube, con qué texto y a qué hora.',
    ruta: '/instagram',
    tinte: 'bg-[#F2ECF5]',
    ilustracion: <CalendarioDePosts fotos={plataformasDe('redes', rubro).map(fotoDe)} />,
  },
  {
    id: 'tienda-online',
    nombre: 'Tienda online',
    explicacion: 'Prepará tus productos con sus fotos y publicalos juntos en tu tienda.',
    ruta: '/lanzamientos',
    tinte: 'bg-[#F4F1E6]',
    ilustracion: <FichaPublicada foto={fotoDe(plataformasDe('catalogos', rubro).at(-1)!)} />,
  },
];

export function PantallaDePublicar({ onElegir }: { onElegir: (ruta: string) => void }) {
  // Entra subiendo desde abajo, como Herramientas.
  const [adentro, setAdentro] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setAdentro(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Publicar"
      className={`fixed inset-x-0 top-0 z-[70] overflow-y-auto overscroll-contain bg-[#F7F7FA] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none md:hidden ${
        adentro ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
      style={{ bottom: ALTO_NAV_INFERIOR, paddingBottom: 24 }}
    >
      <div
        className="sticky top-0 z-10 bg-[#F7F7FA]/90 px-4 pb-3 backdrop-blur-md"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="min-w-0 pt-1">
          <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-[#16181D]">Publicar</h1>
          <p className="mt-0.5 text-[13.5px] leading-snug text-black/50">
            Llevá lo que creaste a tu Instagram y a tu tienda.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-4 pt-1">
        {lugares(rubroMarcaCacheado()).map((l, i) => (
          <button
            key={l.id}
            type="button"
            onClick={() => onElegir(l.ruta)}
            style={{ animationDelay: `${80 + i * 35}ms` }}
            className={`animate-ficha-de-pestana relative flex min-h-[200px] w-full items-center overflow-hidden rounded-[20px] border border-black/[0.06] p-4 text-left transition-transform duration-200 active:scale-[0.99] ${l.tinte}`}
          >
            <span className="relative z-10 flex w-[52%] flex-col">
              <span className="text-[20px] font-bold leading-tight tracking-[-0.02em] text-[#16181D]">{l.nombre}</span>
              <span className="mt-1 text-[13px] leading-snug text-black/55">{l.explicacion}</span>
              <span className="mt-3 inline-flex w-fit items-center gap-1 rounded-full bg-white py-1 pl-3 pr-2 text-[12.5px] font-semibold text-[#16181D]">
                Abrir
                <span aria-hidden="true" className="material-symbols-rounded text-[16px] leading-none">
                  arrow_forward
                </span>
              </span>
            </span>
            <span aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              {l.ilustracion}
            </span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/** Una superficie blanca de la ilustración: la misma sombra y el mismo radio en las dos. */
const SUPERFICIE = 'rounded-[14px] bg-white shadow-[0_10px_24px_rgba(16,24,45,0.14)] ring-1 ring-black/[0.04]';

/**
 * Instagram: la agenda de la semana, con tres posts programados (las fotos de Redes del rubro),
 * cada uno con su día y su hora, que es lo que la sección decide. Una agenda y no la grilla
 * del mes: a este tamaño, en la grilla los posts quedaban de 20px y no se leían.
 */
function CalendarioDePosts({ fotos }: { fotos: string[] }) {
  const posts = [
    { dia: 'Martes', hora: '18:30', foto: fotos[0] },
    { dia: 'Jueves', hora: '12:00', foto: fotos[1] },
    { dia: 'Sábado', hora: '10:15', foto: fotos[2] },
  ];
  return (
    <span className={`block w-[156px] rotate-[3deg] p-2.5 ${SUPERFICIE}`}>
      <span className="flex items-center justify-between px-0.5 pb-1.5">
        <span className="text-[10px] font-bold text-[#16181D]">Esta semana</span>
        <img src={LOGO_INSTAGRAM} alt="" className="h-3.5 w-3.5 rounded-[4px]" />
      </span>
      <span className="flex flex-col gap-1.5">
        {posts.map((p) => (
          <span key={p.dia} className="flex items-center gap-2 rounded-[9px] bg-black/[0.035] p-1">
            <span className="block h-8 w-8 shrink-0 overflow-hidden rounded-[6px]">
              {p.foto && <img src={p.foto} alt="" loading="lazy" className="h-full w-full object-cover" />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col leading-none">
              <span className="text-[9.5px] font-bold text-[#16181D]">{p.dia}</span>
              <span className="mt-1 flex items-center gap-0.5 text-[8.5px] font-medium text-black/45">
                <span className="material-symbols-rounded text-[10px] leading-none">schedule</span>
                {p.hora}
              </span>
            </span>
          </span>
        ))}
      </span>
    </span>
  );
}

/**
 * Tienda online: la ficha de un producto (foto de Catálogos del rubro, nombre y precio en
 * barras, "Publicado") y, al costado, los logos de sus dos canales: lo que hace la sección es
 * dejar el producto listo y mandarlo a la tienda.
 */
function FichaPublicada({ foto }: { foto: string }) {
  return (
    <span className="relative block w-[150px]">
      <span className={`block w-[112px] -rotate-[3deg] overflow-hidden ${SUPERFICIE}`}>
        <span className="block aspect-square overflow-hidden bg-[#F2F2F2]">
          {/* Arriba: las fotos de catálogo son de cuerpo entero y al centro cortaban la cabeza. */}
          <img src={foto} alt="" loading="lazy" className="h-full w-full object-cover object-top" />
        </span>
        <span className="block px-2 pb-2 pt-1.5">
          <span className="block h-[5px] w-[70%] rounded-full bg-black/15" />
          <span className="mt-1.5 flex items-center justify-between gap-1">
            <span className="whitespace-nowrap text-[10px] font-bold tabular-nums text-[#16181D]">$24.900</span>
            <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[#E3F5EA] px-1.5 py-[2px] text-[7.5px] font-bold text-[#1E7A44]">
              <span className="material-symbols-rounded text-[9px] leading-none">check</span>
              Publicado
            </span>
          </span>
        </span>
      </span>
      {/* Los canales, apilados sobre el borde derecho de la ficha: sólo el logo, grande, sobre una
          pastilla blanca. */}
      <span className="absolute -right-1 top-3 flex flex-col gap-2">
        {[
          { logo: LOGO_TIENDANUBE, nombre: 'Tiendanube' },
          { logo: LOGO_MERCADO_LIBRE, nombre: 'Mercado Libre' },
        ].map((c, i) => (
          <span
            key={c.nombre}
            className={`block p-1 ${SUPERFICIE} rounded-[13px] ${i === 0 ? 'rotate-[4deg]' : '-rotate-[3deg]'}`}
          >
            <img src={c.logo} alt="" className="block h-9 w-9 rounded-[9px]" />
          </span>
        ))}
      </span>
    </span>
  );
}
